import { useSyncExternalStore } from 'react';

/** Light or dark: light unless the person chose dark (the mockups are light). */
export type Theme = 'light' | 'dark';

/** The theme the page shows now. */
function shownTheme(): Theme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

/** Tells `changed` each time the page's theme is switched. */
function watchTheme(changed: () => void): () => void {
  const watcher = new MutationObserver(changed);
  watcher.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });
  return () => {
    watcher.disconnect();
  };
}

/** The theme the page shows, the component drawn again when it is switched. */
export function useTheme(): Theme {
  return useSyncExternalStore(watchTheme, shownTheme);
}

const KEY = 'trippath-theme';

/** The theme chosen in this browser; light when none was, or storage cannot be read. */
export function savedTheme(): Theme {
  try {
    return localStorage.getItem(KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

/** Shows `theme`, and remembers it in this browser when it is a choice. */
export function applyTheme(theme: Theme, remember = false): void {
  document.documentElement.dataset.theme = theme;
  if (!remember) return;
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // A private window keeps no choice: the theme holds for this visit only.
  }
}
